import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getCurrentSalonId } from "../utils/currentSalon";
import ModalCloseX from "../components/common/ModalCloseX";
import { getEmailSubscriptionSummary } from "../api/marketingEmailOptIn";
import { getWalkinQueueReportSummary } from "../api/walkinQueue";
import "./Reports.css";
import useMyPermissions from "../hooks/useMyPermissions";
import { formatPermissionError } from "../utils/permissionErrorMessages";

const MANAGER_ROLES = [
  "admin",
  "host",
  "owner",
  "manager",
  "solo_technician",
  "reception",
  "independent_professional",
];

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // Fall back to local time.
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (date, amount) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};



export default function Reports() {
  const navigate = useNavigate();
  const { token, role: ctxRole, user } = useAuth();
  const [error, setError] = useState("");
  const modalText = "text-base md:text-lg";
  const sectionCard =
    "rounded-2xl border border-slate-200 bg-white shadow-sm p-6";
  const actionBtn =
    "mt-4 inline-flex items-center rounded-full bg-rose-500 px-4 py-2 text-sm md:text-base font-semibold text-white hover:bg-rose-600";

  const salonId = getCurrentSalonId(user);
  const role = useMemo(
    () => (ctxRole || localStorage.getItem("role") || "").toLowerCase(),
    [ctxRole]
  );
  const isIndependentProfessional = role === "independent_professional";
  const canViewStaffTeamReports = !isIndependentProfessional;
  const canViewTrainingProgress = useMemo(
    () => ["admin", "host", "owner", "manager"].includes(role),
    [role]
  );
  const canViewCampaignReports = useMemo(
    () =>
      Boolean(user?.is_super_admin) ||
      ["admin", "host", "owner", "manager", "independent_professional"].includes(role),
    [role, user?.is_super_admin]
  );
  const canViewEmailStats = useMemo(
    () =>
      Boolean(user?.is_super_admin) ||
      ["admin", "host", "owner", "manager", "independent_professional"].includes(role),
    [role, user?.is_super_admin]
  );
  const storedToken = token;
  const salonTimeZone = user?.salon_timezone || "America/Toronto";
  const { permissions: effectivePermissions, loading: permissionsLoading } =
    useMyPermissions({ enabled: Boolean(storedToken && salonId) });
  const canAccessReports =
    ["admin", "host"].includes(role) ||
    Boolean(user?.is_super_admin) ||
    (effectivePermissions === null
      ? MANAGER_ROLES.includes(role)
      : Boolean(effectivePermissions?.["reports.view"]));
  const permissionEnabled = (permissionKey, fallback = false) =>
    effectivePermissions === null
      ? fallback
      : Boolean(effectivePermissions?.[permissionKey]);
  const canViewWalkinStats =
    canAccessReports &&
    permissionEnabled("walkin_queue.manage", !isIndependentProfessional);
  const canViewCashDrawerReport =
    canAccessReports &&
    permissionEnabled(
      "cash_drawer.manage",
      Boolean(user?.is_super_admin) ||
        ["admin", "host", "owner", "manager", "reception", "receptionist"].includes(role)
    );
  const todayIso = useMemo(
    () => formatDateISO(new Date(), salonTimeZone),
    [salonTimeZone]
  );
  const emailStartIso = useMemo(
    () => formatDateISO(addDays(new Date(), -29), salonTimeZone),
    [salonTimeZone]
  );
  const walkinStartIso = useMemo(
    () => formatDateISO(addDays(new Date(), -6), salonTimeZone),
    [salonTimeZone]
  );

  const [emailSummary, setEmailSummary] = useState(null);
  const [emailSummaryError, setEmailSummaryError] = useState("");
  const [emailSummaryLoading, setEmailSummaryLoading] = useState(false);
  const [walkinSummary, setWalkinSummary] = useState(null);
  const [walkinSummaryError, setWalkinSummaryError] = useState("");
  const [walkinSummaryLoading, setWalkinSummaryLoading] = useState(false);
  const walkinRevenueTotal = useMemo(() => {
    const rows = walkinSummary?.by_staff || [];
    return rows.reduce((total, row) => total + (Number(row.revenue) || 0), 0);
  }, [walkinSummary]);

  useEffect(() => {
    if (!storedToken) {
      navigate("/login?next=/manager/reports", { replace: true });
      return;
    }
    if (permissionsLoading || effectivePermissions === null) {
      return;
    }

    if (!canAccessReports) {
      navigate("/my-bookings", { replace: true });
    }
    if (!salonId) {
      setError("Select a salon to view reports.");
      const timeout = setTimeout(() => {
        navigate("/my-salons", { replace: true });
      }, 600);
      return () => clearTimeout(timeout);
    }
  }, [
    storedToken,
    salonId,
    navigate,
    permissionsLoading,
    effectivePermissions,
    canAccessReports,
  ]);

  useEffect(() => {
    if (!canViewEmailStats || !salonId || !storedToken) return;
    let isMounted = true;
    setEmailSummaryLoading(true);
    setEmailSummaryError("");
    getEmailSubscriptionSummary(
      {
        salon_id: salonId,
        start_date: emailStartIso,
        end_date: todayIso,
        segment: null,
      },
      storedToken
    )
      .then((data) => {
        if (!isMounted) return;
        setEmailSummary(data || null);
      })
      .catch((err) => {
        if (!isMounted) return;
        setEmailSummaryError(
          err?.response?.data?.detail ||
            "Unable to load email subscription stats."
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setEmailSummaryLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [canViewEmailStats, salonId, storedToken, emailStartIso, todayIso]);

  useEffect(() => {
    if (!canViewWalkinStats || !salonId || !storedToken) return;
    let isMounted = true;
    setWalkinSummaryLoading(true);
    setWalkinSummaryError("");
    getWalkinQueueReportSummary(
      salonId,
      { from_date: walkinStartIso, to_date: todayIso },
      storedToken
    )
      .then((data) => {
        if (!isMounted) return;
        setWalkinSummary(data || null);
      })
      .catch((err) => {
        if (!isMounted) return;
        setWalkinSummaryError(
          formatPermissionError(err, "Unable to load walk-in queue summary.")
        );
      })
      .finally(() => {
        if (!isMounted) return;
        setWalkinSummaryLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [canViewWalkinStats, salonId, storedToken, walkinStartIso, todayIso]);

  const handleClose = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    navigate("/manager/schedule");
  };

  return (
    <div className="reportsPageShell min-h-screen bg-slate-100">
      <div className="reportsPageWide reports-page-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Reports
            </div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>
          <div className={`px-6 py-6 space-y-6 ${modalText}`}>
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className={sectionCard}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Live Dashboard
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Live KPI snapshot with sales, rebooking, utilization, and top
                  services. Change date range to refresh.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/manager/reports/live-dashboard")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={canViewWalkinStats ? sectionCard : "hidden"}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Walk-In Queue
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Turns, fairness, and revenue from walk-ins.
                </p>
                {walkinSummaryError && (
                  <div className="mt-2 text-xs text-rose-600">
                    {walkinSummaryError}
                  </div>
                )}
                {!walkinSummaryError && (
                  <div className="mt-3 grid grid-cols-3 gap-3 text-sm text-slate-700">
                    <div>
                      <div className="text-xs uppercase text-slate-500">
                        Walk-ins
                      </div>
                      <div className="text-base font-semibold text-slate-900">
                        {walkinSummaryLoading ? "--" : walkinSummary?.total_walkins || 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-slate-500">
                        Revenue
                      </div>
                      <div className="text-base font-semibold text-slate-900">
                        {walkinSummaryLoading
                          ? "--"
                          : `$${walkinRevenueTotal.toFixed(2)}`}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase text-slate-500">
                        Overrides
                      </div>
                      <div className="text-base font-semibold text-slate-900">
                        {walkinSummaryLoading
                          ? "--"
                          : walkinSummary?.overrides?.length || 0}
                      </div>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => navigate("/manager/reports/walk-in-queue")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={sectionCard}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Total Sheet
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Run summary cards, employee totals, and export details for a
                  specific date range.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/manager/reports/total-sheet")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={sectionCard}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Profitability Estimate
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Estimate monthly profit using your revenue and your own
                  expense assumptions (tax split-out included).
                </p>
                <button
                  type="button"
                  onClick={() =>
                    navigate("/manager/reports/profitability-estimate")
                  }
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={sectionCard}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Estimate Business Value
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Valuation range + confidence score.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/manager/reports/salon-valuation")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={sectionCard}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Break-Even Sensitivity
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Break-even revenue + scenario analysis.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/manager/reports/break-even-sensitivity")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={canViewStaffTeamReports ? sectionCard : "hidden"}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Sales by Employee
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Review employee sales, tips, refunds, and payment breakdowns with
                  flexible grouping and exports.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/manager/reports/sales-by-employee")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={sectionCard}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Ticket Summary
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Summarize ticket sales by employee with totals and payment
                  breakdowns for a selected date range.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/manager/reports/ticket-summary")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={sectionCard}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Appointment Status Report
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  View appointments by status for a date range. Group by employee,
                  weekday, or time-of-day. Click any appointment to jump to the
                  schedule.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/manager/reports/appointments-status")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={sectionCard}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Cancellations & No-Shows
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Track cancellations, reschedules, and late change attempts. Spot
                  patterns by day, time, and technician and export a data file.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/manager/reports/cancellations")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={canViewStaffTeamReports ? sectionCard : "hidden"}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Maintenance & Cleaning Report
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Analyze expected vs completed cleaning tasks, overdue hotspots,
                  and staff consistency in salon timezone.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/manager/reports/maintenance-cleaning")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              {canViewCampaignReports && (
                <div className={sectionCard}>
                  <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                    Marketing Campaign Performance
                  </h2>
                  <p className="mt-2 text-sm md:text-base text-slate-600">
                    Track campaign performance, conversions, and revenue impact by
                    goal or date range.
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      navigate("/manager/reports/marketing-campaigns")
                    }
                    className={actionBtn}
                  >
                    Open
                  </button>
                </div>
              )}
              {canViewEmailStats && (
                <div className={sectionCard}>
                  <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                    Email Subscription Stats
                  </h2>
                  <p className="mt-2 text-sm md:text-base text-slate-600">
                    Snapshot for the last 30 salon days, including top unsubscribe
                    reason.
                  </p>
                  {emailSummaryError && (
                    <div className="mt-2 text-xs text-rose-600">
                      {emailSummaryError}
                    </div>
                  )}
                  {!emailSummaryError && (
                    <div className="mt-3 grid grid-cols-2 gap-3 text-sm text-slate-700">
                      <div>
                        <div className="text-xs uppercase text-slate-500">
                          Subscribed %
                        </div>
                        <div className="text-base font-semibold text-slate-900">
                          {emailSummaryLoading
                            ? "--"
                            : `${Math.round(
                                (emailSummary?.subscribed_pct || 0) * 100
                              )}%`}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase text-slate-500">
                          New subscribers
                        </div>
                        <div className="text-base font-semibold text-slate-900">
                          {emailSummaryLoading
                            ? "--"
                            : emailSummary?.new_subscribers || 0}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase text-slate-500">
                          Unsubscribes
                        </div>
                        <div className="text-base font-semibold text-slate-900">
                          {emailSummaryLoading
                            ? "--"
                            : emailSummary?.unsubscribes || 0}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase text-slate-500">
                          Top reason
                        </div>
                        <div className="text-base font-semibold text-slate-900">
                          {emailSummaryLoading
                            ? "--"
                            : emailSummary?.top_unsubscribe_reason || "—"}
                        </div>
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => navigate("/manager/reports/email-subscription")}
                    className={actionBtn}
                  >
                    Open
                  </button>
                </div>
              )}
              <div className={sectionCard}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Service Analytics
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Compare service totals, mix by employee, and average duration by
                  service across a date range.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/reports/service-analytics")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={sectionCard}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Category Analytics
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Revenue and mix by service category, staffing signals, growth,
                  concentration, and seasonality.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/reports/category-analytics")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={sectionCard}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Client Analytics
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Track new vs returning clients, lifetime value bands, segments,
                  lapsed outreach lists, and rebooking trends.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/reports/client-analytics")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={sectionCard}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Client Satisfaction
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Average ratings, trends, and technician KPI.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/manager/reports/client-satisfaction")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={sectionCard}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Complaint Themes
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Track the most common feedback themes by staff, category, channel,
                  and time period.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/manager/reports/complaint-themes")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={sectionCard}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Gift Card Analytics
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Track gift card sales, redemptions, balances, client balances,
                  and liability.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/reports/gift-card-analytics")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={sectionCard}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Rewards
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Understand rewards activity, unused balances, and referral bonus
                  performance.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/manager/reports/rewards")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={sectionCard}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Daily Closing Cash Report
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Reconcile daily cash, bonus payouts, and expenses against the
                  Total Sheet for a single salon day.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/manager/reports/daily-closing")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={sectionCard}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Cash Balance Report
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Track daily cash revenue, cash expenses, and current cash balance.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/manager/reports/cash-balance")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={sectionCard}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Tax Liability Summary
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Review taxable vs non-taxable sales and export tax liability by
                  date range and staff.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/manager/reports/tax-liability")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
              <div className={sectionCard}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Sales & Tickets
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Ticket volume, cancellations, booking sources, peak hours, and
                  conversion trends.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/manager/reports/sales-tickets")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>

              {canViewTrainingProgress && (
                <div className={sectionCard}>
                  <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                    Training Progress
                  </h2>
                  <p className="mt-2 text-sm md:text-base text-slate-600">
                    Track staff training completion, overdue items, and progress by role.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate("/manager/reports/training-progress")}
                    className={actionBtn}
                  >
                    Open
                  </button>
                </div>
              )}

              <div className={canViewStaffTeamReports ? sectionCard : "hidden"}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Staff Performance
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Utilization, sales per hour, tips, refunds, and rebooking impact
                  by staff.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/manager/reports/staff-performance")}
                  className={actionBtn}
                >
                  Open
                </button>
              </div>

              <div className={canViewCashDrawerReport ? sectionCard : "hidden"}>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  Cash Drawer Analysis
                </h2>
                <p className="mt-2 text-sm md:text-base text-slate-600">
                  Gift card liability and redemption, plus cash drawer over/short
                  and paid-in/out activity.
                </p>
                <button
                  type="button"
                  onClick={() =>
                    navigate("/manager/reports/gift-cards-cash-drawer")
                  }
                  className={actionBtn}
                >
                  Open
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
